import type {
  CreateTopicBody,
  CreateTopicResponse,
  RouterConfig,
  TopicInfo,
  UpdateTopicAgentsBody,
  UpdateTopicAgentsResponse,
} from '../types/api'
import { api } from './client'

export interface TopicReadOptions {
  /** Bypass the browser HTTP cache for a deliberate authoritative reread. */
  noCache?: boolean
}

function readInit(opts?: TopicReadOptions): RequestInit | undefined {
  return opts?.noCache ? { cache: 'no-store' } : undefined
}

function createTopic(
  workspaceId: string,
  body: CreateTopicBody,
): Promise<CreateTopicResponse> {
  return api.post<CreateTopicResponse>(
    `/api/workspaces/${workspaceId}/topics`,
    body,
  )
}

function updateTopicAgents(
  topicId: string,
  body: UpdateTopicAgentsBody,
): Promise<UpdateTopicAgentsResponse> {
  return api.put<UpdateTopicAgentsResponse>(
    `/api/topics/${topicId}/agents`,
    body,
  )
}

export const topicsApi = {
  listForWorkspace: (workspaceId: string, opts?: TopicReadOptions) =>
    api.get<TopicInfo[]>(
      `/api/workspaces/${workspaceId}/topics`,
      readInit(opts),
    ),
  get: (topicId: string, opts?: TopicReadOptions) =>
    api.get<TopicInfo>(`/api/topics/${topicId}`, readInit(opts)),
  create: createTopic,
  rename: (topicId: string, name: string) =>
    api.patch<void>(`/api/topics/${topicId}`, { name }),
  delete: (topicId: string) => api.delete<void>(`/api/topics/${topicId}`),
  updateAgents: updateTopicAgents,
  setPrimaryAgent: (topicId: string, agentId: string | null) =>
    api.put<void>(`/api/topics/${topicId}/primary-agent`, {
      agent_id: agentId,
    }),
  setRouterConfig: (topicId: string, config: RouterConfig) =>
    api.patch<void>(`/api/topics/${topicId}/router`, config),
  clearRouterConfig: (topicId: string) =>
    api.delete<void>(`/api/topics/${topicId}/router`),
}

/** Plan review actions (topic-scoped resources reviewed inline). */
export const plansApi = {
  approve: (topicId: string, planId: string, annotations?: string) =>
    api.post<void>(
      `/api/topics/${topicId}/plans/${planId}/approve`,
      annotations ? { annotations } : {},
    ),

  reject: (topicId: string, planId: string, reason?: string) =>
    api.post<void>(
      `/api/topics/${topicId}/plans/${planId}/reject`,
      reason ? { reason } : {},
    ),
}

/** Question review actions (topic-scoped resources reviewed inline). */
export const questionsApi = {
  answer: (
    topicId: string,
    questionId: string,
    answer: unknown,
    annotations?: string,
  ) =>
    api.post<void>(`/api/topics/${topicId}/questions/${questionId}/answer`, {
      answer,
      annotations: annotations || undefined,
    }),
}
